import React from 'react';
import * as LucideIcons from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { AtomMark } from './AtomMark';

/**
 * Branded avatar palette — letter-tile DNA matching the WordRush logo.
 * Each entry: id, display name, Lucide icon, and a foreground/background gradient.
 */
export interface AvatarDef {
  id: string;
  name: string;
  iconName: keyof typeof LucideIcons;
  fg: string;
  bg1: string;
  bg2: string;
}

export const AVATAR_LIST: AvatarDef[] = [
  { id: 'ember', name: 'Ember', iconName: 'Flame', fg: '#FFFFFF', bg1: '#FF7A47', bg2: '#FF5A1F' },
  { id: 'pine', name: 'Pine', iconName: 'TentTree', fg: '#FFFFFF', bg1: '#2EB872', bg2: '#16A34A' },
  { id: 'tide', name: 'Tide', iconName: 'Waves', fg: '#FFFFFF', bg1: '#5B9BFF', bg2: '#3B6FE0' },
  { id: 'plum', name: 'Plum', iconName: 'Grape', fg: '#FFFFFF', bg1: '#B66BE6', bg2: '#8B3DC4' },
  { id: 'honey', name: 'Honey', iconName: 'Sun', fg: '#3F2E0A', bg1: '#FFD86E', bg2: '#F59E0B' },
  { id: 'rose', name: 'Rose', iconName: 'Heart', fg: '#FFFFFF', bg1: '#FF7BB4', bg2: '#E0408F' },
  { id: 'mint', name: 'Mint', iconName: 'Leaf', fg: '#062B1E', bg1: '#7EE8C2', bg2: '#34D399' },
  { id: 'cobalt', name: 'Cobalt', iconName: 'Compass', fg: '#FFFFFF', bg1: '#6B7CFF', bg2: '#3D4FCC' },
  { id: 'spark', name: 'Spark', iconName: 'Zap', fg: '#3F2E0A', bg1: '#FFE066', bg2: '#FACC15' },
  { id: 'storm', name: 'Storm', iconName: 'CloudLightning', fg: '#FFFFFF', bg1: '#7B8DA3', bg2: '#4A5568' },
  { id: 'cherry', name: 'Cherry', iconName: 'Cherry', fg: '#FFFFFF', bg1: '#FF6B6B', bg2: '#DC2626' },
  { id: 'moss', name: 'Moss', iconName: 'Sprout', fg: '#FFFFFF', bg1: '#88B948', bg2: '#5D8F2A' },
  { id: 'fox', name: 'Fox', iconName: 'Rabbit', fg: '#FFFFFF', bg1: '#FF9A4D', bg2: '#E8742A' },
  { id: 'sage', name: 'Sage', iconName: 'Feather', fg: '#FFFFFF', bg1: '#5FB7B0', bg2: '#2A8A82' },
  { id: 'iris', name: 'Iris', iconName: 'Flower2', fg: '#FFFFFF', bg1: '#A47BE6', bg2: '#7048CC' },
  { id: 'dune', name: 'Dune', iconName: 'Mountain', fg: '#FFFFFF', bg1: '#D4A373', bg2: '#A47148' },
];

const AVATAR_BY_ID = new Map(AVATAR_LIST.map(a => [a.id, a]));

/** Resolve an avatar by id; falls back to a hash of arbitrary input (e.g. legacy emoji). */
export function resolveAvatar(input?: string | null): AvatarDef {
  if (!input) return AVATAR_LIST[0];
  if (AVATAR_BY_ID.has(input)) return AVATAR_BY_ID.get(input)!;
  // Legacy: hash the input string into the palette so old emoji avatars still pick a stable face
  let h = 0;
  for (let i = 0; i < input.length; i++) h = (h * 31 + input.charCodeAt(i)) | 0;
  return AVATAR_LIST[Math.abs(h) % AVATAR_LIST.length];
}

interface AvatarProps {
  id?: string | null;
  size?: number;
  host?: boolean;
  submitted?: boolean | null;
  ready?: boolean;
  className?: string;
  style?: React.CSSProperties;
  /** Branded avatar to fall back to if `id` is a photo URL that fails to
   *  load. Defaults to a stable hash of the URL so the fallback is at least
   *  consistent for a given user rather than random. */
  fallbackId?: string | null;
}

const PHOTO_URL_RE = /^https?:\/\//i;

/** Detects a URL avatar (Google photoURL / similar) vs. our branded id system. */
export function isPhotoAvatar(input?: string | null): boolean {
  return !!input && PHOTO_URL_RE.test(input.trim());
}

export function Avatar({ id, size = 40, host, submitted, className = '', style, fallbackId }: AvatarProps) {
  const radius = Math.round(size * 0.28);
  const dotSize = Math.max(14, Math.round(size * 0.32));

  // Photo loads fail for real, mundane reasons — a rotated Google URL, an
  // offline phone, a rate-limited lh3 host. The old handler set the <img>
  // to display:none, which left the surface-coloured tile behind it empty:
  // the blank placeholder the user reported. Track the failure and render
  // the branded avatar instead, so a broken photo degrades to a real face
  // rather than a hole.
  const [photoFailed, setPhotoFailed] = React.useState(false);
  React.useEffect(() => { setPhotoFailed(false); }, [id]);

  // Atom — special-case the AI brand mark. Any avatar id resolving to
  // 'atom' (or the literal "Atom") renders the AtomMark component
  // instead of a generic fox emoji. This is what makes Atom's profile
  // recognizably AI everywhere it appears.
  if (id === 'atom' || id === 'Atom') {
    return (
      <div className={className} style={{ display: 'inline-flex', position: 'relative', ...style }}>
        <AtomMark size={size} filled animated />
        {host && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: -2,
              right: -2,
              width: dotSize * 0.55,
              height: dotSize * 0.55,
              borderRadius: '50%',
              background: 'var(--accent)',
              border: '2px solid var(--surface)',
            }}
          />
        )}
      </div>
    );
  }

  // Photo-URL path — render the Google profile photo directly. A coral
  // halo replaces the gradient background for hosts.
  if (isPhotoAvatar(id) && !photoFailed) {
    return (
      <div
        className={`av-tile ${host ? 'host' : ''} ${className}`}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          background: 'var(--surface)',
          overflow: 'hidden',
          boxShadow: host
            ? `0 0 0 2.5px var(--accent), 0 0 0 5px var(--surface), 0 4px 12px rgba(22,22,26,0.18)`
            : `0 2px 6px rgba(22,22,26,0.10), inset 0 0 0 1px var(--line)`,
          ...style,
        }}
        aria-label="Profile photo"
      >
        <img
          src={id!}
          alt=""
          // lh3.googleusercontent.com serves 403 to requests carrying a
          // Referer it doesn't recognise. Without this attribute the photo
          // silently fails to load on a deployed origin while working fine
          // on localhost — which is exactly the shape of "it works on my
          // machine but the live site shows a placeholder".
          referrerPolicy="no-referrer"
          // Google's default photo URL is 96px; ask for one that matches the
          // rendered box on a 3x screen so it isn't a blurry upscale.
          decoding="async"
          loading="lazy"
          width={size}
          height={size}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          onError={() => setPhotoFailed(true)}
        />
        {submitted === true && (
          <div
            style={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              width: dotSize,
              height: dotSize,
              borderRadius: '50%',
              background: 'var(--green)',
              border: '2.5px solid var(--surface)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 2px 4px rgba(22,163,74,0.4)',
            }}
          >
            <LucideIcons.Check size={dotSize * 0.6} strokeWidth={3.4} />
          </div>
        )}
      </div>
    );
  }

  // Branded path. When we got here because a photo URL failed to load, hash
  // the *fallback* (or the URL itself) rather than passing the raw URL to
  // resolveAvatar — a URL hashes to an arbitrary face, but at least a stable
  // one for that user across screens.
  const brandedKey = photoFailed ? (fallbackId || id) : id;
  const avatar = resolveAvatar(brandedKey);
  const Icon = (LucideIcons[avatar.iconName] || LucideIcons.Smile) as LucideIcon;
  const iconSize = Math.round(size * 0.5);

  return (
    <div
      className={`av-tile ${host ? 'host' : ''} ${className}`}
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: `linear-gradient(135deg, ${avatar.bg1} 0%, ${avatar.bg2} 100%)`,
        boxShadow: host
          ? `0 0 0 2.5px var(--accent), 0 0 0 5px var(--surface), 0 4px 12px ${avatar.bg2}55`
          : `0 2px 6px ${avatar.bg2}33, inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -1px 0 rgba(0,0,0,0.10)`,
        ...style,
      }}
      aria-label={avatar.name}
    >
      <Icon size={iconSize} color={avatar.fg} strokeWidth={2.4} />
      {submitted === true && (
        <div
          style={{
            position: 'absolute',
            right: -2,
            bottom: -2,
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            background: 'var(--green)',
            border: '2.5px solid var(--surface)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            boxShadow: '0 2px 4px rgba(22,163,74,0.4)',
          }}
        >
          <LucideIcons.Check size={dotSize * 0.6} strokeWidth={3.4} />
        </div>
      )}
    </div>
  );
}
